import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Main } from '@/components/layout/main';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { DataTable } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/data-table-column-header';

import { handleServerError } from '@/utils/handle-server-error';
import { Check } from 'lucide-react';
import {
  list_crm_contacts,
  create_crm_contact,
  update_crm_contact,
  delete_crm_contact,
  list_crm_tags,
  create_crm_tag,
  delete_crm_tag,
  import_crm_contacts,
  CRMContact,
  CRMContactStatus,
  CRMTag,
} from '@/api/crm';

import { MoreHorizontal, Pencil, Trash2, Eye, Plus, Contact } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { t } from 'i18next';

// Keep API/status values language-independent (English keys) and only translate in the UI.
const STATUS_OPTIONS: CRMContactStatus[] = [
  'new',
  'contacted',
  'interested',
  'follow_up',
  'meeting',
  'negotiation',
  'won',
  'lost',
];

const STATUS_LABEL: Record<CRMContactStatus, string> = {
  new: 'Nouveau',
  contacted: 'Contacté',
  interested: 'Intéressé',
  follow_up: 'Relance',
  meeting: 'RDV',
  negotiation: 'Négociation',
  won: 'Converti',
  lost: 'Perdu',
};

const STATUS_BADGE_CLASS: Record<CRMContactStatus, string> = {
  new: 'bg-slate-100 text-slate-800 border border-slate-200',
  contacted: 'bg-blue-100 text-blue-800 border border-blue-200',
  interested: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  follow_up: 'bg-amber-100 text-amber-900 border border-amber-200',
  meeting: 'bg-purple-100 text-purple-800 border border-purple-200',
  negotiation: 'bg-yellow-100 text-yellow-900 border border-yellow-200',
  won: 'bg-green-100 text-green-800 border border-green-200',
  lost: 'bg-red-100 text-red-800 border border-red-200',
};

function CRMContactsToolbar({ table }: { table: any }) {
  // Column labels shown in "Voir"
  const LABELS: Record<string, string> = {
    name: 'Nom complet',
    phone: 'Téléphone',
    email: 'E-Mail',
    linkedin: 'Linkedin',
    tags: 'Tags',
    status: 'Status',
    actions: 'Actions',
  };

  return (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline">
            <Eye className="mr-2 h-4 w-4" />
            {t('Voir')}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            Basculer les colonnes
          </div>

          {table
            .getAllColumns()
            .filter((col: any) => col.getCanHide?.()) // only hideable cols
            .map((col: any) => {
              const checked = col.getIsVisible?.();
              const label = LABELS[col.id] ?? col.id;

              return (
                <DropdownMenuItem
                  key={col.id}
                  onSelect={(e) => {
                    e.preventDefault(); // keep menu open
                    col.toggleVisibility?.(!checked);
                  }}
                  className="flex items-center gap-2"
                >
                  {/* checkmark like your screenshot */}
                  <span className="w-4 flex items-center justify-center">
                    {checked ? <Check className="h-4 w-4" /> : null}
                  </span>

                  <span>{label}</span>
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function SuperAdminCRMView() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  // data
  const [contacts, setContacts] = useState<CRMContact[]>([]);
  const [tags, setTags] = useState<CRMTag[]>([]);

  // search
  const [q, setQ] = useState('');

  // DataTable pagination params (same pattern as CabinetsManagement)
  const [params, setParams] = useState<{ page: number; page_size: number }>({ page: 0, page_size: 10 });

  // Create contact
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [status, setStatus] = useState<CRMContactStatus>('new');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  // Tags create
  const [newTag, setNewTag] = useState('');
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);

  // Import
  const [importFile, setImportFile] = useState<File | null>(null);
  const [createMissingTags, setCreateMissingTags] = useState(true);


  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<CRMContact | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLinkedin, setEditLinkedin] = useState('');
  const [editStatus, setEditStatus] = useState<CRMContactStatus>('new');
  const [editTagIds, setEditTagIds] = useState<string[]>([]);

  const tagNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const tg of tags) m.set(tg.id, tg.name);
    return m;
  }, [tags]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [c, tgs] = await Promise.all([list_crm_contacts(), list_crm_tags()]);
      setContacts(c);
      setTags(tgs);
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  };
  const resetCreateForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setLinkedin('');
    setStatus('new');
    setSelectedTagIds([]);
  };
  useEffect(() => {
    fetchAll();
  }, []);

  // reset to first page when search changes or page_size changes
  useEffect(() => {
    setParams((p) => ({ ...p, page: 0 }));
  }, [q]);

  const filteredContacts = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return contacts;

    return contacts.filter((c) => {
      const tagText = (c.tags ?? []).map((id) => tagNameById.get(id) ?? '').join(' ');
      const hay = `${c.name ?? ''} ${c.phone ?? ''} ${c.email ?? ''} ${c.linkedin ?? ''} ${tagText}`.toLowerCase();
      return hay.includes(s);
    });
  }, [contacts, q, tagNameById]);

  // Inside crm.tsx
  // Inside crm.tsx
  const pagesCnt = useMemo(() => {
    const size = params.page_size;
    return Math.max(1, Math.ceil(filteredContacts.length / size));
  }, [filteredContacts.length, params.page_size]);

  // ✅ data paginée (client-side)
  // Inside crm.tsx
  // Inside crm.tsx
  const pagedContacts = useMemo(() => {
    // table.tsx sends 'params.page' as the offset (index * size).
    // Use it directly.
    const start = params.page;
    return filteredContacts.slice(start, start + params.page_size);
  }, [filteredContacts, params.page, params.page_size]);
  // actions
  const handleCreateContact = async () => {
    if (!name.trim()) {
      alert('Name is required');
      return;
    }
    try {
      setLoading(true);
      await create_crm_contact({
        name: name.trim(),
        phone: phone.trim() || null,
        email: email.trim() || null,
        linkedin: linkedin.trim() || null,
        status,
        tags: selectedTagIds,
      });

      setName('');
      setPhone('');
      setEmail('');
      setLinkedin('');
      setStatus('new');
      setSelectedTagIds([]);

      await fetchAll();
      // ✅ close dialog + reset
      setShowCreateContactDialog(false);
      resetCreateForm();
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (id: string) => {
    if (!confirm('Delete this contact?')) return;
    try {
      await delete_crm_contact(id);
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      handleServerError(err);
    }
  };

  const handleStatusChange = async (id: string, next: CRMContactStatus) => {
    try {
      await update_crm_contact(id, { status: next });
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status: next } : c)));
    } catch (err) {
      handleServerError(err);
    }
  };

  const openEdit = (c: CRMContact) => {
    setEditing(c);
    setEditName(c.name ?? '');
    setEditPhone(c.phone ?? '');
    setEditEmail(c.email ?? '');
    setEditLinkedin(c.linkedin ?? '');
    setEditStatus(c.status);
    setEditTagIds(c.tags ?? []);
    setEditOpen(true);
  };

  const handleUpdateContact = async () => {
    if (!editing) return;
    if (!editName.trim()) {
      alert('Name is required');
      return;
    }

    try {
      setLoading(true);
      await update_crm_contact(editing.id, {
        name: editName.trim(),
        phone: editPhone.trim() || null,
        email: editEmail.trim() || null,
        linkedin: editLinkedin.trim() || null,
        status: editStatus,
        tags: editTagIds,
      });

      setEditOpen(false);
      setEditing(null);
      await fetchAll();
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.trim()) return;
    try {
      setLoading(true);
      await create_crm_tag({ name: newTag.trim() });
      setNewTag('');
      await fetchAll();
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (id: string) => {
    if (!confirm('Delete this tag?')) return;
    try {
      await delete_crm_tag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      setSelectedTagIds((prev) => prev.filter((x) => x !== id));
      setEditTagIds((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      handleServerError(err);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Choose a file first (.csv or .xlsx)');
      return;
    }

    try {
      setLoading(true);
      const res = await import_crm_contacts(importFile, {
        create_missing_tags: createMissingTags,
        tag_separator: ',', // ✅ fixed, backend default
      });

      alert(
        `Import done ✅\nProcessed: ${res.processed}\nSaved/Merged: ${res.saved_or_merged}\nSkipped: ${res.skipped_invalid}\nCreated tags: ${res.created_tags}`
      );

      if (res.errors?.length) console.log('Import row errors:', res.errors);

      setImportFile(null);
      await fetchAll();
    } catch (err) {
      handleServerError(err);
    } finally {
      setLoading(false);
    }
  };
  const PAGE_SIZES = [10, 20, 30, 40, 50, 100] as const;


  const setTableParams = (updater: any) => {
    setParams((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;

      const nextSize = next?.page_size ?? prev.page_size;

      // table.tsx uses "page" as OFFSET (0, 10, 20...) => keep it numeric
      let nextPage = Number(next?.page ?? prev.page);
      if (!Number.isFinite(nextPage) || nextPage < 0) nextPage = 0;

      // ✅ if page size changed, always restart from 0
      const sizeChanged = nextSize !== prev.page_size;
      if (sizeChanged) nextPage = 0;

      return { page: nextPage, page_size: nextSize };
    });
  };
  const currentPage = Math.floor(params.page / params.page_size) + 1;
  const canPrevPage = params.page > 0;
  const canNextPage = currentPage < pagesCnt;

  const goToPage = (pageIndex: number) => {
    const safePageIndex = Math.max(0, Math.min(pageIndex, pagesCnt - 1));
    setParams((prev) => ({
      ...prev,
      page: safePageIndex * prev.page_size,
    }));
  };

  const changePageSize = (nextSize: number) => {
    setParams({
      page: 0,
      page_size: nextSize,
    });
  };
  // DataTable columns 
  const columns: ColumnDef<CRMContact>[] = useMemo(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('Nom_complet')} />,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground font-mono">#{c.id}</span>
            </div>
          );
        },
      },
      {
        id: 'phone',
        accessorKey: 'phone',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('Telephone')} />,
        cell: ({ row }) => <span>{row.original.phone ?? '-'}</span>,
      },
      {
        id: 'email',
        accessorKey: 'email',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('Email')} />,
        cell: ({ row }) => <span>{row.original.email ?? '-'}</span>,
      },
      {
        id: 'linkedin',
        accessorKey: 'linkedin',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('LinkedIn')} />,
        cell: ({ row }) => {
          const url = row.original.linkedin;
          return url ? (
            <a href={url} target="_blank" rel="noreferrer" className="underline text-primary">
              Ouvrir
            </a>
          ) : (
            <span className="text-muted-foreground">-</span>
          );
        },
      },
      {
        id: 'tags',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('Tags')} />,
        cell: ({ row }) => {
          const ids = row.original.tags ?? [];
          if (!ids.length) return <span className="text-muted-foreground">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {ids.map((id) => (
                <Badge key={id} variant="secondary">
                  {tagNameById.get(id) ?? '—'}
                </Badge>
              ))}
            </div>
          );
        },
      }, {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => <DataTableColumnHeader column={column} title={t('Status')} />,
        cell: ({ row }) => {
          const c = row.original;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="p-0 h-auto w-fit hover:bg-transparent">
                  <Badge className={`cursor-pointer ${STATUS_BADGE_CLASS[c.status]}`}>
                    {STATUS_LABEL[c.status]}
                  </Badge>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="start">
                {STATUS_OPTIONS.map((s) => (
                  <DropdownMenuItem
                    key={s}
                    onClick={() => handleStatusChange(c.id, s)}
                    className="flex items-center gap-2"
                  >
                    <span className={`inline-flex h-2 w-2 rounded-full ${STATUS_BADGE_CLASS[s].includes('green') ? 'bg-green-500'
                      : STATUS_BADGE_CLASS[s].includes('red') ? 'bg-red-500'
                        : STATUS_BADGE_CLASS[s].includes('blue') ? 'bg-blue-500'
                          : STATUS_BADGE_CLASS[s].includes('indigo') ? 'bg-indigo-500'
                            : STATUS_BADGE_CLASS[s].includes('purple') ? 'bg-purple-500'
                              : STATUS_BADGE_CLASS[s].includes('amber') ? 'bg-amber-500'
                                : STATUS_BADGE_CLASS[s].includes('yellow') ? 'bg-yellow-500'
                                  : 'bg-slate-500'
                      }`} />
                    <span className={s === c.status ? 'font-semibold' : ''}>{STATUS_LABEL[s]}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-center">{t('Actions')}</div>,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div className="flex justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      if (c.linkedin) window.open(c.linkedin, '_blank', 'noreferrer');
                    }}
                    disabled={!c.linkedin}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Ouvrir LinkedIn
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => openEdit(c)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Modifier
                  </DropdownMenuItem>

                  <DropdownMenuItem onClick={() => handleDeleteContact(c.id)} className="text-red-600">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [tagNameById]
  );
  console.log("params", params, "pagesCnt", pagesCnt, "shown", pagedContacts.length);
  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">
        <Main>
          <div className="space-y-6">
            <Dialog open={showCreateContactDialog} onOpenChange={(open) => {
              setShowCreateContactDialog(open);
              if (!open) resetCreateForm();
            }}>
              <PageHeader
                leading={
                  <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Contact className="h-5 w-5" />
                  </span>
                }
                title="CRM"
                description="Contacts management"
                actions={
                  <Button onClick={() => setShowCreateContactDialog(true)}>
                    <Plus className="me-2 h-4 w-4" />
                    {t('Create_contact')}
                  </Button>
                }
              />

                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Créer un contact</DialogTitle>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">Nom *</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                      </div>

                      <div>
                        <label className="text-sm font-medium">Téléphone</label>
                        <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                      </div>

                      <div>
                        <label className="text-sm font-medium">Email</label>
                        <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                      </div>

                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">LinkedIn</label>
                        <Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
                      </div>

                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">Status</label>
                        <select
                          className="w-full border rounded-md p-2 bg-white"
                          value={status}
                          onChange={(e) => setStatus(e.target.value as CRMContactStatus)}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="text-sm font-medium">Tags</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {tags.map((tg) => {
                            const active = selectedTagIds.includes(tg.id);
                            return (
                              <button
                                key={tg.id}
                                type="button"
                                className={`border rounded-full px-3 py-1 text-sm ${active ? 'bg-black text-white' : 'bg-white'
                                  }`}
                                onClick={() => {
                                  setSelectedTagIds((prev) =>
                                    prev.includes(tg.id) ? prev.filter((x) => x !== tg.id) : [...prev, tg.id]
                                  );
                                }}
                              >
                                {tg.name}
                              </button>
                            );
                          })}
                          {!tags.length && <span className="text-sm text-muted-foreground">No tags yet.</span>}
                        </div>
                      </div>
                    </div>

                    <DialogFooter className="gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowCreateContactDialog(false);
                          resetCreateForm();
                        }}
                      >
                        Annuler
                      </Button>

                      <Button onClick={handleCreateContact} disabled={loading || !name.trim()}>
                        {loading ? 'Saving...' : 'Créer'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

            {/* Search + Table */}
            <Card>
              <CardHeader className="space-y-2">
                <CardTitle>{t('Contacts_List')}</CardTitle>
                <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                  <div className="w-full md:max-w-lg">
                    <Input
                      placeholder={t('Search_crm')}
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {t('Total')}: <b>{filteredContacts.length}</b>
                  </div>
                </div>
              </CardHeader>
            </Card>
            <DataTable
              columns={columns}
              data={pagedContacts}
              pages={pagesCnt}
              params={params}
              setParams={setTableParams}
              showPagination={false}
              storageKey="super-admin-crm-contacts-table"
              ToolbarName={CRMContactsToolbar}
            />

            {filteredContacts.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-4 px-1 sm:flex-nowrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{t('rows_per_page')}</span>
                  <select
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                    value={params.page_size}
                    onChange={(e) => changePageSize(Number(e.target.value))}
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-sm font-medium">
                  {t('Page_a')} {currentPage} {t('sur_a')} {pagesCnt}
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={() => goToPage(0)}
                    disabled={!canPrevPage}
                  >
                    «
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={() => goToPage(currentPage - 2)}
                    disabled={!canPrevPage}
                  >
                    ‹
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={() => goToPage(currentPage)}
                    disabled={!canNextPage}
                  >
                    ›
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 w-8 p-0"
                    onClick={() => goToPage(pagesCnt - 1)}
                    disabled={!canNextPage}
                  >
                    »
                  </Button>
                </div>
              </div>
            )}

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>{t('Tags_a')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input placeholder={t('New_tag_name')} value={newTag} onChange={(e) => setNewTag(e.target.value)} />
                  <Button onClick={handleCreateTag} disabled={loading}>
                    {t('Add')}
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {tags.map((tg) => (
                    <div
                      key={tg.id}
                      className="flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-1 text-sm text-foreground"
                    >
                      <span className="max-w-[220px] truncate">{tg.name}</span>

                      <button
                        type="button"
                        onClick={() => handleDeleteTag(tg.id)}
                        className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-destructive hover:bg-destructive/10 focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`Delete tag ${tg.name}`}
                        title="Supprimer"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {!tags.length && <span className="text-sm text-muted-foreground">No tags yet.</span>}
                </div>
              </CardContent>
            </Card>

            {/* Import */}
            <Card>
              <CardHeader>
                <CardTitle>{t('Import_CSV_XLSX')}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-3">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                />

                {/* Expected format helper */}
                <div className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{t('Expected_file_format')}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('First_row_must_be_headers')}
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const headers = ['name', 'phone', 'email', 'linkedin', 'instagram', 'status', 'tags'];
                        const exampleRows = [
                          ['Imad Bouzkraoui', '+33612345678', 'imad@mail.com', 'https://linkedin.com/in/imad', '', 'new', 'client,VIP'],
                          ['Sara Benali', '0612345678', 'sara@mail.com', '', '@sarabenali', 'contacted', 'prospect'],
                        ];

                        // CSV builder with simple escaping
                        const escape = (v: string) => {
                          const s = String(v ?? '');
                          if (s.includes(',') || s.includes('"') || s.includes('\n')) {
                            return `"${s.replace(/"/g, '""')}"`;
                          }
                          return s;
                        };

                        const csv = [
                          headers.join(','),
                          ...exampleRows.map((r) => r.map(escape).join(',')),
                        ].join('\n');

                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'crm_import_example.csv';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      }}
                    >
                      {t('Download_example_csv')}
                    </Button>
                  </div>

                  <div className="overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left p-2">{t('Column')}</th>
                          <th className="text-left p-2">{t('Required')}</th>
                          <th className="text-left p-2">{t('Notes')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-2 font-mono">name</td>
                          <td className="p-2">
                            <span className="text-xs rounded-full px-2 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400">
                              {t('Yes')}
                            </span>
                          </td>
                          <td className="p-2 text-muted-foreground text-xs">
                            {t('Contact_full_name')}
                          </td>
                        </tr>

                        {[
                          { col: 'phone', note: t('Phone_number_optional_recommended') },
                          { col: 'email', note: t('Email_optional') },
                          { col: 'linkedin', note: t('LinkedIn_url_optional') },
                          { col: 'instagram', note: t('Instagram_handle_optional') },
                          { col: 'status', note: t('Status_optional_ex_new_contacted') },
                          { col: 'tags', note: t('Tags_optional_separated_by_comma') },
                        ].map((x) => (
                          <tr key={x.col} className="border-t">
                            <td className="p-2 font-mono">{x.col}</td>
                            <td className="p-2">
                              <span className="text-xs rounded-full px-2 py-0.5 bg-green-500/10 text-green-700 dark:text-green-400">
                                {t('No')}
                              </span>
                            </td>
                            <td className="p-2 text-muted-foreground text-xs">{x.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">{t('Tags_hint')}:</span>{' '}
                    {t('If_multiple_tags_use_separator_like_comma')}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Switch checked={createMissingTags} onCheckedChange={setCreateMissingTags} />
                  <span className="text-sm">{t('Create_missing_tags')}</span>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleImport} disabled={loading || !importFile}>
                    {loading ? t('Importing') : t('Import')}
                  </Button>
                </div>
              </CardContent>
            </Card>


          </div>

          {/* Edit Dialog */}
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Modifier le contact</DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Nom *</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>

                <div>
                  <label className="text-sm font-medium">numero de telephone</label>
                  <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                </div>

                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium">LinkedIn</label>
                  <Input value={editLinkedin} onChange={(e) => setEditLinkedin(e.target.value)} />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    className="w-full border rounded-md p-2 bg-white"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as CRMContactStatus)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium">Tags</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tags.map((tg) => {
                      const active = editTagIds.includes(tg.id);
                      return (
                        <button
                          key={tg.id}
                          type="button"
                          className={`border rounded-full px-3 py-1 text-sm ${active ? 'bg-black text-white' : 'bg-white'
                            }`}
                          onClick={() => {
                            setEditTagIds((prev) =>
                              prev.includes(tg.id) ? prev.filter((x) => x !== tg.id) : [...prev, tg.id]
                            );
                          }}
                        >
                          {tg.name}
                        </button>
                      );
                    })}
                    {!tags.length && <span className="text-sm text-muted-foreground">No tags yet.</span>}
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setEditOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleUpdateContact} disabled={loading}>
                  {loading ? 'Saving...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}